import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { StatusPill, errorText, formatWhen } from "./common";

// Run log: every automation run with its per-step log (/api/automations/runs).

const STATUSES = ["", "Waiting", "Running", "Success", "Failed", "Skipped", "Cancelled"];

export default function RunLog({ api, showNotice, automations }) {
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState("");
  const [automationId, setAutomationId] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/automations/runs", { params: { status: status || undefined, automation_id: automationId || undefined, limit: 100 } });
      setRuns(res.data.runs || []);
    } catch (err) {
      showNotice(errorText(err, "Failed to load runs"), "error");
    }
  }, [api, showNotice, status, automationId]);

  useEffect(() => { load(); }, [load]);

  const show = async (run) => {
    if (open?.id === run.id) { setOpen(null); return; }
    try { setOpen((await api.get(`/automations/runs/${run.id}`)).data); } catch (err) { showNotice(errorText(err, "Could not load the run"), "error"); }
  };
  const cancel = async (run) => {
    try { await api.post(`/automations/runs/${run.id}/cancel`); showNotice("Run cancelled", "success"); setOpen(null); load(); } catch (err) { showNotice(errorText(err, "Could not cancel"), "error"); }
  };

  return (
    <section className="panel glass-panel" data-testid="automation-run-log">
      <div className="panel-header">
        <h3>Run log</h3>
        <span>
          <select value={automationId} onChange={(e) => setAutomationId(e.target.value)}>
            <option value="">All automations</option>
            {automations.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{s || "Any status"}</option>)}</select>
          <button className="ghost-btn small" onClick={load}><RefreshCw size={14} /> Refresh</button>
        </span>
      </div>
      <div className="compact-list">
        {runs.map((run) => (
          <div key={run.id}>
            <div className="compact-row" data-testid={`run-${run.id}`} style={{ cursor: "pointer" }} onClick={() => show(run)}>
              <StatusPill status={run.status} />
              <div>
                <strong>{run.automation_name}</strong>
                <span>{run.trigger} • {formatWhen(run.created_at)}{run.status === "Waiting" && run.next_run_at ? ` • next step ${formatWhen(run.next_run_at)}` : ""}{run.error ? ` • ${run.error}` : ""}{run.skip_reason ? ` • ${run.skip_reason}` : ""}</span>
              </div>
              {run.status === "Waiting" && <button className="ghost-btn small" onClick={(e) => { e.stopPropagation(); cancel(run); }}>Cancel</button>}
            </div>
            {open?.id === run.id && (
              <div className="panel" style={{ margin: "4px 0 10px 24px", padding: 10 }} data-testid="run-detail">
                {(open.steps || []).map((s, i) => (
                  <div key={i} className="compact-row"><StatusPill status={s.status} /><span>{s.index + 1}. {s.type}{s.attempt > 1 ? ` (attempt ${s.attempt})` : ""} - {s.detail} <em>{formatWhen(s.at)}</em></span></div>
                ))}
                {!open.steps?.length && <span className="muted">No steps recorded.</span>}
              </div>
            )}
          </div>
        ))}
        {!runs.length && <div className="empty-cell">No runs match.</div>}
      </div>
    </section>
  );
}
